import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import InboxFilterComponentMC from './InboxFilterComponentMC';
import useInboxFiltersUI from '../../hooks/inbox/ui/useInboxFiltersUI';
import { getFilteredOptionsByComponent } from '../../../util/paginationUtils';
import TitleMC from '../../components/TitleMC';
import ButtonMC from '../../components/ButtonMC';

/**
 * InboxAdvancedFiltersMC - Organism Component (Presentational)
 *
 * Organism component para filtros avanzados del inbox.
 * Componente puramente presentacional con toda la lógica de negocio
 * encapsulada en useInboxFiltersUI hook.
 *
 * PRINCIPIOS SEGUIDOS:
 * - Organism atómico puramente presentacional
 * - Separación total de lógica de negocio y presentación
 * - useInboxFiltersUI maneja toda la lógica de negocio
 * - Este componente solo se encarga del renderizado
 * - Sin estado local de negocio, solo presentación
 * - Usa componentes atómicos optimizados (InboxFilterComponentMC)
 *
 * OPTIMIZACIONES:
 * - React.memo para evitar re-renders innecesarios
 * - Lógica de negocio completamente separada
 * - Renderizado optimizado con componentes atómicos
 * - Sin efectos secundarios en el componente
 * - Componentes de filtros memoizados individualmente
 *
 * @param {Object} filter - Estado actual de filtros (incluye searchTerm y advancedFilters)
 * @param {function} onFilterChange - Handler para cambios de filtros (incluye searchTerm)
 * @param {function} onClose - Handler para cerrar pantalla filtros
 */
const InboxAdvancedFiltersMC = memo(({
  filter,
  onFilterChange,
  onClose
}) => {
  const { t } = useTranslation('InboxMC');

  // Hook que encapsula TODA la lógica de negocio
  // Usa filter.advancedFilters como valor por defecto para los filtros dinámicos
  const {
    activeFilters,
    filtersConfig,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters
  } = useInboxFiltersUI(filter.advancedFilters || {}, onFilterChange);

  return (
    <div className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white md:tw-w-80">
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-p-4 tw-space-y-4">
        {filtersConfig.map((filterConfig, key) => {
          const filteredOptions = getFilteredOptionsByComponent(
            filterConfig,
            activeFilters,
            filtersConfig
          );

          return (
            <div key={filterConfig.key} className="tw-mb-4">
              <TitleMC text={filterConfig.label} variant="SECTION" />
              <InboxFilterComponentMC
                filter={{ ...filterConfig, options: filteredOptions, label: '' }}
                componentKey={key}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
              />
            </div>
          );
        })}
      </div>
      <div className="tw-shrink-0 tw-p-4 tw-border-t tw-space-y-2">
        <ButtonMC
          variant="PRIMARY"
          text={t('filters.buttons.apply')}
          onClick={() => {
            handleApplyFilters();
            onClose?.();
          }}
          className="tw-w-full"
        />
        <ButtonMC
          variant="SECONDARY"
          text={t('filters.buttons.clear')}
          onClick={() => {
            handleClearFilters();
            onClose?.();
          }}
          className="tw-w-full"
        />
      </div>
    </div>
  );
});

export default InboxAdvancedFiltersMC;
